import { COMMUNITY_API_PREFIX } from 'shared/api/constants/api-constants'
import { httpClient } from 'shared/api/http-client'
import { ApiPaginationResponseInterface } from 'shared/types/api-pagination-response-interface'
import { addQueryParam } from 'shared/utils/add-query-param'
import { BanMemberRequestBody, MemberInterface } from 'modules/community/types/member-interface'
import { PostCommentsInfoInterface } from 'modules/community/types/post-info'
import {
  ApiSearchPaginationResponseInterface,
  PostInterface,
  SearchPostInterface,
} from 'modules/community/types/post-interface'

const PAGINATION_ORDER_KEY = 'pagination[order]'
const PAGINATION_START_FROM_KEY = 'pagination[startFrom]'
const PAGINATION_LIMIT_KEY = 'pagination[limit]'

export const getPostsByLastId = (communityPath: string, lastPostId: number) => {
  const searchQuery = new URLSearchParams()

  addQueryParam(searchQuery, PAGINATION_LIMIT_KEY, '10')
  addQueryParam(searchQuery, PAGINATION_ORDER_KEY, 'desc')
  addQueryParam(searchQuery, PAGINATION_START_FROM_KEY, lastPostId === 0 ? undefined : lastPostId)

  return httpClient.get<ApiPaginationResponseInterface<PostInterface>>(
    `${COMMUNITY_API_PREFIX}/${communityPath}/posts/list?${searchQuery.toString()}`,
  )
}
export const getScheduledPostsByCursor = (communityPath: string, lastPostId: number) => {
  const searchQuery = new URLSearchParams()

  addQueryParam(searchQuery, PAGINATION_LIMIT_KEY, '10')
  addQueryParam(searchQuery, PAGINATION_ORDER_KEY, 'prev')
  addQueryParam(searchQuery, PAGINATION_START_FROM_KEY, lastPostId === 0 ? undefined : lastPostId)

  return httpClient.get<ApiPaginationResponseInterface<PostInterface>>(
    `${COMMUNITY_API_PREFIX}/${communityPath}/moderation/posts/scheduled/list?${searchQuery.toString()}`,
  )
}

export const getTopicPostsByLastId = (
  communityPath: string,
  topicPath: string,
  lastPostId: number,
) => {
  const searchQuery = new URLSearchParams()

  addQueryParam(searchQuery, PAGINATION_LIMIT_KEY, '10')
  addQueryParam(searchQuery, PAGINATION_ORDER_KEY, 'desc')
  addQueryParam(searchQuery, PAGINATION_START_FROM_KEY, lastPostId === 0 ? undefined : lastPostId)

  return httpClient.get<ApiPaginationResponseInterface<PostInterface>>(
    `${COMMUNITY_API_PREFIX}/${communityPath}/topic/${topicPath}/posts/list?${searchQuery.toString()}`,
  )
}

export const declinePost = (communityPath: string, postId: number[]) =>
  httpClient.post(`${COMMUNITY_API_PREFIX}/${communityPath}/moderation/posts/decline`, {
    ids: postId,
  })

export function getAllPendingPostsByLastId(
  communityPath: string,
  lastPostId: number,
  filter: string,
  controller: AbortController,
) {
  const searchQuery = new URLSearchParams()

  addQueryParam(searchQuery, PAGINATION_LIMIT_KEY, '10')
  addQueryParam(searchQuery, PAGINATION_ORDER_KEY, 'desc')
  addQueryParam(searchQuery, PAGINATION_START_FROM_KEY, lastPostId === 0 ? undefined : lastPostId)
  addQueryParam(searchQuery, 'filter[searchBy]', filter)

  return httpClient.get<ApiPaginationResponseInterface<PostInterface>>(
    `${COMMUNITY_API_PREFIX}/${communityPath}/moderation/posts/list?${searchQuery.toString()}`,
    {
      signal: controller?.signal,
    },
  )
}

export const getOwnPendingPostsByLastId = (communityPath: string, lastPostId: number) => {
  const searchQuery = new URLSearchParams()

  addQueryParam(searchQuery, PAGINATION_LIMIT_KEY, '10')
  addQueryParam(searchQuery, PAGINATION_ORDER_KEY, 'desc')
  addQueryParam(searchQuery, PAGINATION_START_FROM_KEY, lastPostId === 0 ? undefined : lastPostId)

  return httpClient.get<ApiPaginationResponseInterface<PostInterface>>(
    `${COMMUNITY_API_PREFIX}/${communityPath}/my-pending-posts/list?${searchQuery.toString()}`,
  )
}

export const deletePost = (postId: number) =>
  httpClient.delete<void>(`/api/community/post/${postId}`)

export const banUser = (communityPath: string, userId: number, body?: BanMemberRequestBody) =>
  httpClient.post(`${COMMUNITY_API_PREFIX}/${communityPath}/banned-users/${userId}`, body || {})

export const unbanUser = (communityPath: string, userId: number) =>
  httpClient.delete(`${COMMUNITY_API_PREFIX}/${communityPath}/banned-users/${userId}`)

export const getPostsCommentsInfo = (ids: number[]) => {
  const searchQuery = new URLSearchParams()

  ids.forEach(id => searchQuery.append('post_id[]', `${id}`))

  return httpClient.get<ApiPaginationResponseInterface<PostCommentsInfoInterface>>(
    `/api/comment/post_infos?${searchQuery.toString()}`,
  )
}

export const deleteUser = (communityPath: string, userId: number) =>
  httpClient.delete<MemberInterface>(`${COMMUNITY_API_PREFIX}/${communityPath}/member/${userId}`)

export const searchPosts = ({
  communityPath,
  q,
  scrollId,
  limit,
  topicPath,
}: {
  communityPath: string
  q: string
  scrollId: string | null
  limit: number
  topicPath?: string
}) => {
  const searchQuery = new URLSearchParams()

  addQueryParam(searchQuery, 'pagination[startFrom]', scrollId)
  addQueryParam(searchQuery, 'searchBy', q)
  addQueryParam(searchQuery, 'pagination[limit]', limit)
  addQueryParam(searchQuery, 'topicPath', topicPath)

  return httpClient.get<ApiSearchPaginationResponseInterface<SearchPostInterface>>(
    `${COMMUNITY_API_PREFIX}/${communityPath}/search?${searchQuery.toString()}`,
  )
}
